/**
 * Notifications — mobile-parity notification list with tabs (All / Unread /
 * Buddies), sectioned by Today / Earlier, and a "Mark all as read" overflow menu.
 * Tapping a post-related notification opens a post detail modal (CommentsModal
 * with fullView), mirroring the mobile PostDetails screen.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useFeed } from '@wlb/shared/context/FeedContext';
import feedService from '@wlb/shared/api/services/feedService';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { FeedPost } from '@wlb/shared/types/post';
import type { NotificationItem as NotificationItemType } from '@wlb/shared/types/notification';
import { ListStatus } from '../components/ui/ListStatus';
import { Tabs } from '../components/ui/Tabs';
import { PopoverMenu, type PopoverMenuItem } from '../components/ui/PopoverMenu';
import { BellIcon, ChevronLeftIcon, MoreIcon } from '../components/ui/icons';
import { useNotifications } from '../features/notifications/useNotifications';
import { useNotificationBadge } from '../features/notifications/NotificationBadgeContext';
import { NotificationItem } from '../features/notifications/NotificationItem';
import { NotificationsSkeleton } from '../features/notifications/NotificationsSkeleton';
import { CommentsModal } from '../features/feed/CommentsModal';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import '../components/ui/skeleton.css';
import '../features/profile/profile.css';
import '../features/notifications/notifications.css';

type NotificationTab = 'all' | 'unread' | 'buddies';
type MenuKey = 'markAllAsRead';

export function Notifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCachedPost } = useFeed();
  const openUserProfile = useOpenUserProfile();
  const {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    acceptBuddyRequest,
    rejectBuddyRequest,
    joinGroupRequest,
  } = useNotifications();
  const notificationBadge = useNotificationBadge();

  /** Reading a row here changes the nav badge, which nothing else will notice. */
  const markRowRead = async (id: string): Promise<boolean> => {
    const success = await markAsRead(id);
    if (success) notificationBadge?.refresh().catch(() => undefined);
    return success;
  };

  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [detailPost, setDetailPost] = useState<FeedPost | null>(null);
  const [detailCommentId, setDetailCommentId] = useState<string | null>(null);
  const [openingPostId, setOpeningPostId] = useState<string | null>(null);

  const tabItems = useMemo(
    () => [
      { key: 'all' as const, label: t('home.notifications.tabs.all') },
      { key: 'unread' as const, label: t('home.notifications.tabs.unread') },
      { key: 'buddies' as const, label: t('home.notifications.tabs.buddies') },
    ],
    [t],
  );

  const menuItems: PopoverMenuItem<MenuKey>[] = [
    { key: 'markAllAsRead', label: t('home.notifications.markAllAsRead'), icon: BellIcon },
  ];

  const filtered = useMemo(() => {
    switch (activeTab) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'buddies':
        return notifications.filter(n => n.type === 'buddyRequest');
      default:
        return notifications;
    }
  }, [notifications, activeTab]);

  const sections = useMemo(() => {
    const today = filtered.filter(n => n.section?.toLowerCase() === 'today');
    const earlier = filtered.filter(n => n.section?.toLowerCase() !== 'today');
    const list: { title: string; data: typeof filtered }[] = [];
    if (today.length > 0) list.push({ title: t('home.notifications.sections.today'), data: today });
    if (earlier.length > 0) list.push({ title: t('home.notifications.sections.earlier'), data: earlier });
    return list;
  }, [filtered, t]);

  const handlePress = async (item: NotificationItemType) => {
    if (openingPostId) return;
    if (!item.isRead) {
      const success = await markRowRead(item.id);
      if (!success) return;
    }

    // Buddy invite notifications are matched by name, not by resolved type:
    // "Buddy Invite Accepted" is classified as 'general' because it no longer
    // carries action buttons, so the type check below misses it. Mobile handles
    // these first and so does the web.
    const typeName = (item.notificationTypeName || '').trim().toLowerCase();

    if (typeName === 'buddy invite received') {
      navigate('/connections?tab=buddies&subtab=requested');
      return;
    }

    if (typeName === 'buddy invite accepted') {
      navigate('/connections?tab=buddies&subtab=accepted');
      return;
    }

    // Chat notification → open the conversation.
    if (item.conversationId) {
      navigate(`/messages/${item.conversationId}`);
      return;
    }

    // Post-related notification (like, comment, group post) → open post detail
    // modal. The feed cache only holds the currently loaded page, so fall back
    // to GetPost the way mobile's PostDetails screen does.
    if (item.postId) {
      const postId = String(item.postId);
      const commentId = item.postCommentId != null ? String(item.postCommentId) : null;
      const cached = getCachedPost?.(postId);
      if (cached) {
        setDetailCommentId(commentId);
        setDetailPost(cached);
        return;
      }

      setOpeningPostId(postId);
      try {
        const response = await feedService.getPostDetails(postId);
        if (response.success && response.data) {
          setDetailCommentId(commentId);
          setDetailPost(response.data.post);
        } else {
          ToastService.show({ type: 'error', message: t('home.notifications.postNotFound') });
        }
      } catch {
        ToastService.show({ type: 'error', message: t('home.notifications.postNotFound') });
      } finally {
        setOpeningPostId(null);
      }
      return;
    }

    // Group-only notification — web has no group detail screen yet.
    if (item.groupId) {
      ToastService.show({ type: 'info', message: t('home.notifications.toasts.comingSoon') });
      return;
    }

    // Buddy request (resolved type) → open the Buddies / Requested tab so the
    // user can respond. The name-based checks above already caught the named
    // variants; this catches any buddyRequest-typed row that slipped through.
    if (item.type === 'buddyRequest') {
      navigate('/connections?tab=buddies&subtab=requested');
      return;
    }

    // Rows about a person and no content (follow, profile activity) open the
    // actor. Rows carrying no actor either — system notices — stay inert.
    if (item.actorUserGuid) {
      openUserProfile(item.actorUserGuid);
    }
  };

  const handleView = (item: NotificationItemType) => {
    handlePress(item).catch(() => undefined);
  };

  const handleOpenProfile = async (item: NotificationItemType) => {
    if (!item.isRead) {
      const success = await markRowRead(item.id);
      if (!success) return;
    }
    openUserProfile(item.actorUserGuid);
  };

  const handleAvatarPress = (item: NotificationItemType) => {
    handleOpenProfile(item).catch(() => undefined);
  };

  const handleMenuSelect = async (key: MenuKey) => {
    setMenuAnchorRect(null);
    if (key === 'markAllAsRead') {
      if (markingAll) return;
      setMarkingAll(true);
      try {
        const success = await markAllAsRead();
        if (success) {
          notificationBadge?.refresh().catch(() => undefined);
          ToastService.show({ type: 'success', message: t('home.notifications.markAllAsRead') });
        }
      } finally {
        setMarkingAll(false);
      }
    }
  };

  return (
    <section className="notifications-page" aria-label={t('home.notifications.title')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('home.notifications.accessibility.back')}
          onClick={() => navigate(-1)}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('home.notifications.title')}</h1>
        <button
          type="button"
          className="profile-topbar__button"
          aria-label={t('home.notifications.accessibility.openMenu')}
          aria-haspopup="dialog"
          aria-expanded={Boolean(menuAnchorRect)}
          onClick={event => setMenuAnchorRect(event.currentTarget.getBoundingClientRect())}
        >
          <MoreIcon size={20} />
        </button>
      </div>

      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={setActiveTab}
        label={t('home.notifications.accessibility.notificationsList')}
        idPrefix="notif-tab"
      />

      <div
        role="tabpanel"
        id={`notif-tab-panel-${activeTab}`}
        aria-labelledby={`notif-tab-${activeTab}`}
      >
        {openingPostId && (
          <p className="notification-loading" role="status">{t('common.loading')}</p>
        )}

        {loading && notifications.length === 0 && <NotificationsSkeleton />}

        {!loading && error && notifications.length === 0 && (
          <ListStatus
            variant="error"
            icon={<BellIcon size={48} />}
            title={t('home.notifications.loadFailed')}
            message={error}
            onAction={() => { fetchNotifications().catch(() => undefined); }}
            busy={loading}
          />
        )}

        {!loading && !error && filtered.length === 0 && (
          <ListStatus
            variant="empty"
            icon={<BellIcon size={48} />}
            title={t('home.notifications.emptyTitle')}
            message={t('home.notifications.emptyDescription')}
            onAction={() => { fetchNotifications().catch(() => undefined); }}
            busy={loading}
          />
        )}

        {sections.map(section => (
          <div className="notification-section skeleton-fade-in" key={section.title}>
            <h2 className="notification-section__header">{section.title}</h2>
            {section.data.map(item => (
              <NotificationItem
                key={item.id}
                notification={item}
                onPress={handlePress}
                onAccept={acceptBuddyRequest}
                onReject={rejectBuddyRequest}
                onJoin={joinGroupRequest}
                onView={handleView}
                onOpenProfile={handleAvatarPress}
              />
            ))}
          </div>
        ))}
      </div>

      <PopoverMenu
        anchorRect={menuAnchorRect}
        items={menuItems}
        label={t('home.notifications.accessibility.openMenu')}
        labelledBy="notif-menu-title"
        onClose={() => setMenuAnchorRect(null)}
        onSelect={key => { handleMenuSelect(key).catch(() => undefined); }}
      />

      {detailPost && (
        <CommentsModal
          post={detailPost}
          fullView
          highlightCommentId={detailCommentId}
          onClose={() => { setDetailPost(null); setDetailCommentId(null); }}
          onCommentAdded={() => undefined}
        />
      )}
    </section>
  );
}
